import { useRef, useState } from 'react'
import ar from '../../locales/ar.json'
import { validateFiles } from '../../utils/validation'

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf', 'video/mp4']
const MAX_FILE_SIZE = 10 * 1024 * 1024

export default function FileUpload({
  id,
  label,
  accept = ALLOWED_TYPES.join(','),
  multiple = false,
  files = [],
  onAdd,
  onRemove,
  hint,
  error,
  required = false,
}) {
  const inputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)
  const [localError, setLocalError] = useState(null)

  const handleFiles = (newFiles) => {
    setLocalError(null)
    const fileList = Array.from(newFiles)
    const err = validateFiles([...(files || []), ...fileList])
    if (err) { setLocalError(err); return }
    onAdd(multiple ? fileList : [fileList[0]])
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    handleFiles(e.dataTransfer.files)
  }

  const displayError = error || localError

  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={id} className="label-base">
          {label}
          {required && <span className="text-red-500 mr-1" aria-hidden="true">*</span>}
        </label>
      )}

      <div
        role="button"
        tabIndex={0}
        aria-label={label || ar.app.download}
        className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center gap-2 pointer-events-none">
          <svg className="w-10 h-10 text-gold opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          <p className="text-sm text-stone-500">
            اسحب الملفات هنا أو <span className="text-gold-dark font-medium">اضغط للاختيار</span>
          </p>
          {hint && <p className="text-xs text-stone-400">{hint}</p>}
        </div>
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          multiple={multiple}
          className="sr-only"
          onChange={(e) => handleFiles(e.target.files)}
          aria-describedby={displayError ? `${id}-error` : undefined}
        />
      </div>

      {displayError && (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600 flex items-center gap-1" role="alert">
          <span aria-hidden="true">⚠</span> {displayError}
        </p>
      )}

      {/* File list */}
      {files?.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm">
              <span className="flex items-center gap-2 text-stone-700 truncate">
                <FileIcon type={f.type} />
                <span className="truncate max-w-xs">{f.name}</span>
                <span className="text-xs text-stone-400 whitespace-nowrap">
                  ({(f.size / 1024 / 1024).toFixed(1)} MB)
                </span>
              </span>
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="text-red-500 hover:text-red-700 mr-2 flex-shrink-0"
                aria-label={`حذف ${f.name}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function FileIcon({ type }) {
  if (type?.startsWith('image/'))   return <span>🖼️</span>
  if (type === 'application/pdf')   return <span>📄</span>
  if (type?.startsWith('video/'))   return <span>🎥</span>
  return <span>📎</span>
}
